import React, { createContext, Dispatch, SetStateAction, useContext, useState } from "react";
import { getClubsByOrgStatsId, getMajorClubs } from "../api/ClubApi";
import { BigsClub } from "../types/personnel";

type AffiliationContext = {
    affiliationDropdownOptions: Array<AffiliationDropdownOptionType>;
    setAffiliationDropdownOptions: Dispatch<SetStateAction<AffiliationDropdownOptionType[]>>;
    /** Club pickers: teams for an org from {@code bigs.club} (Stats team id as value). */
    refreshClubDropdownByOrgStatsId: (orgStatsId: number) => void;
    /** Club pickers: major clubs from {@code bigs.club}. */
    refreshClubDropdownAllMajors: () => void;
};

type AffiliationDropdownOptionType = {
    value: number;
    label: string;
    teamId: number;
    orgId: number;
    clubStatsId?: number;
};

const clubToDropdownOption = (club: BigsClub): AffiliationDropdownOptionType => {
    const level = club.level ? club.level.trim() : "";
    const label =
        level === "MLB" ? `${club.name} (Major)` : level ? `${club.name} (${level})` : club.name;
    return {
        value: club.clubStatsId,
        label,
        teamId: club.clubStatsId,
        orgId: club.orgStatsId,
        clubStatsId: club.clubStatsId,
    };
};

const AffiliationContext = createContext<AffiliationContext>({
    affiliationDropdownOptions: [],
    setAffiliationDropdownOptions: null,
    refreshClubDropdownByOrgStatsId: null,
    refreshClubDropdownAllMajors: null,
});

const AffiliationContextProvider: React.FC = ({ children }) => {
    const [affiliationDropdownOptions, setAffiliationDropdownOptions] = useState<
        AffiliationDropdownOptionType[]
    >([]);

    const refreshClubDropdownByOrgStatsId = (orgStatsId: number) => {
        getClubsByOrgStatsId(orgStatsId)
            .then((response: BigsClub[]) => {
                const options = (response || [])
                    .map(clubToDropdownOption)
                    .sort((a, b) => a.label.localeCompare(b.label));
                setAffiliationDropdownOptions(options);
            })
            .catch(() => {
                setAffiliationDropdownOptions([]);
            });
    };

    const refreshClubDropdownAllMajors = () => {
        getMajorClubs()
            .then((response: BigsClub[]) => {
                const options = (response || [])
                    .map(clubToDropdownOption)
                    .sort((a, b) => a.label.localeCompare(b.label));
                setAffiliationDropdownOptions(options);
            })
            .catch(() => {
                setAffiliationDropdownOptions([]);
            });
    };

    return (
        <AffiliationContext.Provider
            value={{
                affiliationDropdownOptions,
                setAffiliationDropdownOptions,
                refreshClubDropdownByOrgStatsId,
                refreshClubDropdownAllMajors,
            }}
        >
            {children}
        </AffiliationContext.Provider>
    );
};

const useAffiliation = (): AffiliationContext => {
    const context = useContext<AffiliationContext>(AffiliationContext);
    if (context === undefined) {
        throw new Error(`useAffiliation must be used within a AffiliationContextProvider`);
    }
    return context;
};

export { AffiliationContextProvider, AffiliationDropdownOptionType, useAffiliation };
